import { CONSTITUTION_TYPES, type ProteinProduct, type ConstitutionType, type ConstitutionInfo } from '@/lib/index';
import { IMAGES } from '@/assets/images';

export interface DiagnosisQuestion {
  id: number;
  text: string;
  options: {
    text: string;
    type: ConstitutionType;
  }[];
}

export const constitutionData: Record<ConstitutionType, ConstitutionInfo> = CONSTITUTION_TYPES;

export const proteinProducts: ProteinProduct[] = [
  {
    id: 'prod-earth',
    name: '단백환 地 (Earth)',
    element: '地',
    targetConstitution: '태음인',
    tagline: '체내 순환과 저장된 기운의 조화로운 배출',
    description: '흡수력이 뛰어나 체중이 늘기 쉬운 태음인을 위해, 순환을 돕고 노폐물 배출에 탁월한 한방 성분을 배합했습니다.',
    keyBenefits: [
      '원활한 체내 순환 지원',
      '습담(노폐물) 제거 도움',
      '근육량 유지 및 체지방 관리',
      '지구력 강화 성분 포함'
    ],
    mainIngredients: ['의이인(율무)', '산약(마)', '대두단백', '완두단백'],
    price: 85000,
    imageUrl: IMAGES.PRODUCT_JI
  },
  {
    id: 'prod-water',
    name: '단백환 水 (Water)',
    element: '水',
    targetConstitution: '소음인',
    tagline: '차가운 속을 데워주는 따뜻한 영양 공급',
    description: '소화기가 약하고 몸이 찬 소음인을 위해, 소화 부담을 최소화하고 위장 기능을 돕는 성분을 강화했습니다.',
    keyBenefits: [
      '소화 효소 배합으로 흡수율 극대화',
      '기초 대사량 증진 도움',
      '복부 온기 유지 지원',
      '근감소증 예방 특화'
    ],
    mainIngredients: ['인삼', '백출', '가수분해 유청단백', '난백단백'],
    price: 88000,
    imageUrl: IMAGES.PRODUCT_SU
  },
  {
    id: 'prod-fire',
    name: '단백환 火 (Fire)',
    element: '火',
    targetConstitution: '소양인',
    tagline: '상체의 열을 내리고 신장 기운을 보강',
    description: '열이 많고 신장 기능이 약해지기 쉬운 소양인을 위해, 열을 식히고 하체 근력을 강화하는 성분을 담았습니다.',
    keyBenefits: [
      '상체 열감 완화 지원',
      '신장 및 하체 기능 보강',
      '빠른 에너지 회복',
      '근육 피로 개선 성분 포함'
    ],
    mainIngredients: ['숙지황', '산수유', '유청단백분리물(WPI)', '녹두단백'],
    price: 82000,
    imageUrl: IMAGES.PRODUCT_HWA
  },
  {
    id: 'prod-wind',
    name: '단백환 風 (Wind)',
    element: '風',
    targetConstitution: '태양인',
    tagline: '솟구치는 기운을 다스리는 간 기능 보강',
    description: '폐 기능은 강하나 간 기능이 약해 기운이 위로 솟는 태양인을 위해, 간 기운을 돕고 기를 맑게 하는 성분을 선별했습니다.',
    keyBenefits: [
      '간 기능 보호 및 해독 지원',
      '상기되는 기운 하강 유도',
      '근육 합성 효율 최적화',
      '심신 안정 및 집중력 향상'
    ],
    mainIngredients: ['오가피', '모과', '쌀단백', '대마종자단백'],
    price: 92000,
    imageUrl: IMAGES.PRODUCT_PUNG
  }
];

export const diagnosisQuestions: DiagnosisQuestion[] = [
  {
    id: 1,
    text: '본인의 체형 중 가장 발달한 부위는 어디인가요?',
    options: [
      { text: '가슴 부위가 넓고 목덜미가 굵으며 하체가 가늘다', type: '태양인' },
      { text: '허리 부위가 굵고 전체적으로 체격이 건실하다', type: '태음인' },
      { text: '어깨와 가슴이 발달했으나 엉덩이와 하체가 빈약하다', type: '소양인' },
      { text: '상체보다는 엉덩이와 하체가 발달하고 전체적으로 균형 잡혔다', type: '소음인' }
    ]
  },
  {
    id: 2,
    text: '평소 소화 상태나 식습관은 어떠신가요?',
    options: [
      { text: '소화는 잘 되지만 가끔 음식이 목에 걸리는 느낌이 있다', type: '태양인' },
      { text: '대식가이며 무엇이든 잘 먹고 소화력이 아주 좋다', type: '태음인' },
      { text: '식욕이 왕성하고 음식을 빨리 먹는 편이며 열이 잘 오른다', type: '소양인' },
      { text: '소화기가 약해 적게 먹는 편이며 자주 체한다', type: '소음인' }
    ]
  },
  {
    id: 3,
    text: '땀을 흘렸을 때의 컨디션은 어떤가요?',
    options: [
      { text: '땀이 별로 없으며 땀을 많이 흘리면 기운이 빠진다', type: '태양인' },
      { text: '땀이 많은 편이며 땀을 흘리고 나면 몸이 가뿐하다', type: '태음인' },
      { text: '땀이 잘 나지 않지만 나더라도 컨디션에 큰 영향이 없다', type: '소양인' },
      { text: '땀이 나면 유독 피곤하고 기운이 없어지는 것을 느낀다', type: '소음인' }
    ]
  },
  {
    id: 4,
    text: '평소 성격이나 의사결정 방식은 어떠신가요?',
    options: [
      { text: '창의적이고 과감하며 리더십이 강하지만 독단적일 때가 있다', type: '태양인' },
      { text: '꾸준하고 참을성이 많으며 보수적이고 변화를 신중히 검토한다', type: '태음인' },
      { text: '민첩하고 활동적이며 직선적이고 성격이 다소 급한 편이다', type: '소양인' },
      { text: '꼼꼼하고 내성적이며 계획적이고 실수를 줄이려 노력한다', type: '소음인' }
    ]
  },
  {
    id: 5,
    text: '배변 습관이나 상태는 어떠신가요?',
    options: [
      { text: '대변 양이 많고 건강하지만 가끔 불규칙하다', type: '태양인' },
      { text: '설사를 자주 하지 않으며 변비가 생기기 쉬운 편이다', type: '태음인' },
      { text: '변비가 있으면 가슴이 답답하고 열이 오르는 느낌이 든다', type: '소양인' },
      { text: '대변이 묽은 편이며 설사를 하면 기운이 매우 떨어진다', type: '소음인' }
    ]
  },
  {
    id: 6,
    text: '추위와 더위 중 무엇을 더 힘들어하시나요?',
    options: [
      { text: '더위를 잘 타지 않으며 기운이 위로 솟는 느낌을 자주 받는다', type: '태양인' },
      { text: '더위를 많이 타고 땀을 많이 흘리며 습한 날씨를 싫어한다', type: '태음인' },
      { text: '겨울에도 찬물을 마실 정도로 열이 많고 더위를 못 참는다', type: '소양인' },
      { text: '추위를 몹시 타며 손발이 차고 따뜻한 것을 좋아한다', type: '소음인' }
    ]
  },
  {
    id: 7,
    text: '단백질 섭취 후 나타나는 반응은 어떤가요? (경험 기반)',
    options: [
      { text: '섭취 후 머리가 무겁거나 기운이 뜨는 느낌이 들 때가 있다', type: '태양인' },
      { text: '소화는 잘 되지만 과하게 먹으면 몸이 무거워지고 붓는다', type: '태음인' },
      { text: '피부에 트러블이 생기거나 갈증이 심해지는 경우가 있다', type: '소양인' },
      { text: '가스가 자주 차거나 더부룩해서 지속적으로 먹기 힘들다', type: '소음인' }
    ]
  },
  {
    id: 8,
    text: '일상적인 활동량과 피로 회복 속도는 어떤가요?',
    options: [
      { text: '순간적인 에너지는 좋으나 금방 소진되는 느낌이다', type: '태양인' },
      { text: '지구력이 좋아 오래 일하지만 한번 피곤하면 오래간다', type: '태음인' },
      { text: '활동량이 매우 많고 지치지 않지만 하체가 피로할 때가 많다', type: '소양인' },
      { text: '조금만 활동해도 금방 피로해지며 충분한 휴식이 필요하다', type: '소음인' }
    ]
  }
];